<template>
  <div class="bg-gray-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container max-w-6xl">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-2">Moje ocene</h1>
        <p class="text-gray-400">
          Pregled svih ocena koje ste dobili kao prodavac ili kupac
        </p>
      </div>

      <!-- Loader -->
      <div v-if="showMainLoader" class="flex justify-center items-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="bg-red-900 text-white p-4 rounded-lg mb-6">
        {{ error }}
      </div>

      <!-- Main Content -->
      <template v-else-if="currentUser">
        <!-- Rating stats -->
        <MyRatingStats
          :stats="ratingStats || defaultRatingStats"
          :userRole="userRole"
          :activeFilter="ratingFilter"
          @change-role="handleRoleChange"
          @change-filter="handleFilterChange"
        />

        <!-- Ratings list with built-in loading and pagination -->
        <MyRatingsList
          :ratings="filteredRatings"
          :loading="ratingsLoading"
          :current-page="ratingCurrentPage"
          :meta="ratingsMeta || defaultMeta"
          :user-role="userRole"
          :rating-filter="ratingFilter"
          @change-page="handlePageChange"
        />
      </template>

      <!-- Not authenticated -->
      <div v-else class="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
        <p class="mb-4">Morate biti ulogovani da biste videli svoje ocene.</p>
        <router-link
          to="/login"
          class="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Prijavite se
        </router-link>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :title="toast.title"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useUserRatings } from '@/composables/useUserRatings';
import { debounce } from '@/utils/debounce';
import ToastNotification from '@/components/ToastNotification.vue';

// Import custom components for ratings page
import MyRatingStats from '@/components/ratings/MyRatingStats.vue';
import MyRatingsList from '@/components/ratings/MyRatingsList.vue';

// Default data for when ratings haven't loaded yet
const defaultRatingStats = {
  seller: { likes: 0, dislikes: 0 },
  buyer: { likes: 0, dislikes: 0 },
};

const defaultMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

type RatingFilter = 'all' | 'positive' | 'negative';

export default defineComponent({
  name: 'RatingsView',
  components: {
    ToastNotification,
    MyRatingStats,
    MyRatingsList,
  },
  setup() {
    const store = useStore();

    // Main state
    const loading = ref(true);
    const showMainLoader = ref(false);
    const error = ref<string | null>(null);
    const ratingFilter = ref<RatingFilter>('all');
    const toast = ref({
      show: false,
      message: '',
      type: 'info' as 'info' | 'success' | 'warning' | 'error',
      title: '',
    });

    // Get current user from store
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const userId = computed(() => currentUser.value?.id || null);

    // Debounced main loader functions
    const debouncedShowMainLoader = debounce(() => {
      showMainLoader.value = true;
    }, 400);

    const hideMainLoaderImmediately = () => {
      debouncedShowMainLoader.cancel();
      showMainLoader.value = false;
    };

    // Watch main loading state
    watch(loading, (isLoading) => {
      if (isLoading) {
        debouncedShowMainLoader();
      } else {
        hideMainLoaderImmediately();
      }
    });

    // User ratings functionality
    const {
      ratings,
      stats: ratingStats,
      loading: ratingsLoading,
      meta: ratingsMeta,
      currentPage: ratingCurrentPage,
      userRole,
      fetchRatings,
      fetchRatingStats,
      changePage: changeRatingPage,
      changeUserRole: changeRatingRole,
    } = useUserRatings(userId);

    // Filtered ratings based on selected filter
    const filteredRatings = computed(() => {
      if (!ratings.value) return [];

      if (ratingFilter.value === 'all') {
        return ratings.value;
      } else if (ratingFilter.value === 'positive') {
        return ratings.value.filter((rating) => rating.isLike);
      } else {
        return ratings.value.filter((rating) => !rating.isLike);
      }
    });

    // Handle role change
    const handleRoleChange = (role: 'seller' | 'buyer') => {
      ratingFilter.value = 'all';
      changeRatingRole(role);
    };

    // Handle filter change
    const handleFilterChange = (filter: RatingFilter) => {
      ratingFilter.value = filter;
    };

    // Handle page change
    const handlePageChange = (page: number) => {
      changeRatingPage(page);
    };

    // Show toast message
    const showToast = (
      message: string,
      type: 'info' | 'success' | 'warning' | 'error' = 'info',
      title?: string
    ) => {
      toast.value = {
        show: true,
        message,
        type,
        title: title || '',
      };
    };

    // Initialize data
    const initializeData = async () => {
      if (!userId.value) {
        loading.value = false;
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        // Load both ratings and stats
        await Promise.all([
          fetchRatings(1, userRole.value),
          fetchRatingStats(),
        ]);
      } catch (err) {
        console.error('Error initializing ratings data:', err);
        error.value = 'Greška prilikom učitavanja podataka o ocenama.';
        showToast(
          'Greška prilikom učitavanja podataka o ocenama.',
          'error',
          'Greška'
        );
      } finally {
        loading.value = false;
      }
    };

    // Watch for user changes
    watch(userId, (newUserId) => {
      if (newUserId) {
        initializeData();
      } else {
        loading.value = false;
      }
    });

    onMounted(() => {
      initializeData();
    });

    onBeforeUnmount(() => {
      debouncedShowMainLoader.cancel();
    });

    return {
      // State
      currentUser,
      loading,
      showMainLoader,
      error,
      ratingFilter,
      toast,

      // Ratings data
      ratings,
      ratingStats,
      ratingsLoading,
      ratingsMeta,
      ratingCurrentPage,
      userRole,
      filteredRatings,

      // Default data
      defaultRatingStats,
      defaultMeta,

      // Methods
      handleRoleChange,
      handleFilterChange,
      handlePageChange,
    };
  },
});
</script>
